import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { AppHeaderContext } from '@deltek/specification-client/core/Contexts/AppHeaderContext'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import { isDeltekUserorAdmin } from '@deltek/specification-client/core/Widgets/ProductListing/productsUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FirmQuery } from 'api/graphQL/queries/firm.queries'
import { ReportEmbedQuery } from 'api/graphQL/queries/reportEmbed.queries'
import { FirmResponse } from 'api/graphQL/types/firm.types'
import clsx from 'clsx'
import { EmbedReportConfigResponse } from 'models/reportEmbed'
import { Embed, models, Report, service } from 'powerbi-client'
import { PowerBIEmbed } from 'powerbi-client-react'
import React, { useEffect, useState } from 'react'

const title = 'Industry Analytics'
const useStyles = makeStyles((theme: Theme) => ({
    reportStyle: {
        height: 800,
        width: 1350,
        position: 'relative',
        left: theme.spacing(32),
        top: theme.spacing(2.4),
        bottom: theme.spacing(2.4),
        '& iframe': {
            border: 0,
        },
    },
    mainContainer: {
        width: '100%',
    },
}))

const SpecpointAnalyticsPage: React.FC = () => {
    const { updateAppHeaderContent } = React.useContext(AppHeaderContext)
    const classes = useStyles()

    //this firm id isn't valid so we can pass it in as a filter to prevent any data from showing in the report
    const excludeAllFirmsId = '00000000-0000-0000-0000-00000000000'
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const { data: firmResponse, loading: firmResponseLoading } = useQuery<
        FirmResponse
    >(FirmQuery, {
        variables: {
            id:
                currentUserInfoResponse?.currentUserInfo?.account?.accountId ??
                '',
        },
        skip: !currentUserInfoResponse,
    })

    //default filter to be applied on "Analytics Home" page
    const homeFilterId = {
        $schema: 'http://powerbi.com/product/schema#basic',
        target: {
            table: 'BPM Firms',
            column: 'Firm Id',
        },
        operator: 'In',
        values: [
            currentUserInfoResponse?.currentUserInfo.account?.accountId ??
                excludeAllFirmsId,
        ],
        filterType: models.FilterType.Basic,
        requireSingleSelection: true,
    }
    const homeFilterReseller = {
        $schema: 'http://powerbi.com/product/schema#basic',
        target: {
            table: 'BPM Firms',
            column: 'Reseller',
        },
        operator: 'In',
        values: [firmResponse?.firm.displayName ?? ''],
        filterType: models.FilterType.Basic,
        requireSingleSelection: true,
    }

    //default filter for the "Competition" page
    const competitionFilter = {
        $schema: 'http://powerbi.com/product/schema#basic',
        target: {
            table: 'BPM Firms',
            column: 'Firm Id',
        },
        displaySettings: { isHiddenInViewMode: true },
        operator: 'In',
        values: [
            currentUserInfoResponse?.currentUserInfo.account?.accountId ??
                excludeAllFirmsId,
        ],
        requireSingleSelection: false,
        filterType: models.FilterType.Basic,
    }

    // PowerBI Report object (to be received via callback)
    const [report, setReport] = React.useState<Report>()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        getEmbedReport,
        { loading: reportDataLoading, data: reportData },
    ] = useLazyQuery<EmbedReportConfigResponse>(ReportEmbedQuery, {
        displayName: 'getReportEmbedQuery',
        fetchPolicy: 'no-cache',
        onError: () => {
            showGlobalSnackbar({
                variables: {
                    message: 'Unable to load Power BI report from service.',
                    variant: 'error',
                    id: 'SpecpointAnalytics_GetEmbedReport_ResponseError',
                },
            })
        },
    })

    const [reportConfig, setReportConfig] = useState<
        models.IReportEmbedConfiguration
    >({
        type: 'report',
        id: undefined,
        embedUrl: undefined,
        accessToken: undefined,
        tokenType: models.TokenType.Embed,
        settings: {
            panes: {
                filters: {
                    expanded: false,
                    visible: false,
                },
            },
            layoutType: models.LayoutType.Custom,
            customLayout: {
                displayOption: models.DisplayOption.FitToPage,
            },
            navContentPaneEnabled: false,
        },

        filters: [
            //This is a default filter to speed up the initial load.
            //It's applied at the report-level and applies to all pages.
            {
                $schema: 'http://powerbi.com/product/schema#basic',
                target: {
                    table: 'BPM Firms',
                    column: 'Firm Id',
                },
                displaySettings: { isHiddenInViewMode: true },
                operator: 'In',
                values: [
                    currentUserInfoResponse?.currentUserInfo.account
                        ?.accountId ?? excludeAllFirmsId,
                ],
                filterType: models.FilterType.Basic,
                requireSingleSelection: false,
            },
        ],
    })

    const eventHandlersMap = new Map([
        [
            'loaded',
            function (event: any, report: any) {
                console.log('Report has loaded')
                if (report) {
                    setFilters(report)
                }
            },
        ],
        [
            'rendered',
            function (event: any, report: any) {
                console.log('Report has rendered')
            },
        ],
        [
            'buttonClicked',
            function (event: any, report: any) {
                //These buttons in the report apply a bookmark which overwrites our firm filters.
                //So we reapply them to make sure the families/pts show up.
                if (event) {
                    if (
                        event.detail.title === 'Reset Competition' ||
                        event.detail.title == 'Comptition - Home'
                    ) {
                        setFilters(report)
                    }
                }
            },
        ],
        [
            'error',
            function (event?: service.ICustomEvent<any>) {
                if (event) {
                    console.error(event.detail)
                }
            },
        ],
    ])

    // Update filter options based on the filters selected
    React.useMemo(() => {
        if (reportData && !reportDataLoading) {
            setReportConfig({
                ...reportConfig,
                embedUrl:
                    reportData?.getEmbeddedAnalyticsReport.embedReport.embedUrl,
                accessToken:
                    reportData?.getEmbeddedAnalyticsReport.embedToken.token,
            })
        }
    }, [reportData, reportDataLoading])

    async function setFilters(powerbiReport: Report): Promise<void> {
        //Remove the initial report-level filter added in the config
        //so that we can add it at the page level
        powerbiReport.updateFilters(models.FiltersOperations.RemoveAll)

        await powerbiReport.getPages().then((pages) => {
            if (isDeltekUserorAdmin(currentUserInfoResponse).isDeltekUser) {
                // deltek user sees all
                pages[0].getVisuals().then((homeVisuals) => {
                    homeVisuals.forEach((visual) => {
                        if (visual.title === 'Choose a company') {
                            visual.setVisualDisplayState(
                                models.VisualContainerDisplayMode.Visible
                            )
                        } else if (visual.title === 'Company Name') {
                            //Hide the firm name that overlaps the company chooser
                            visual.setVisualDisplayState(
                                models.VisualContainerDisplayMode.Hidden
                            )
                        }
                    })
                })
            } else if (firmResponse?.firm.isReseller) {
                //reseller can only get firms for which they are a reseller
                pages[0].getVisuals().then((homeVisuals) => {
                    homeVisuals.forEach((visual) => {
                        if (visual.title === 'Choose a company') {
                            visual.setVisualDisplayState(
                                models.VisualContainerDisplayMode.Visible
                            )
                        } else if (visual.title === 'Company Name') {
                            //Hide the firm name that overlaps the company chooser
                            visual.setVisualDisplayState(
                                models.VisualContainerDisplayMode.Hidden
                            )
                        }
                    })
                })

                pages[0].getFilters().then((homeFilters) => {
                    pages[0].updateFilters(models.FiltersOperations.Replace, [
                        homeFilterReseller,
                    ])
                })
            } else {
                //regular bpm can only see their data
                pages[0].getVisuals().then((homeVisuals) => {
                    homeVisuals.forEach(async (visual) => {
                        //clearing the default (Blank) selection set in report design
                        if (visual.title === 'Choose a company') {
                            visual.setSlicerState({
                                filters: [],
                            })
                        }
                    })
                })

                pages[0].getFilters().then((homeFilters) => {
                    pages[0].updateFilters(models.FiltersOperations.Replace, [
                        homeFilterId,
                    ])
                })

                pages[1].getVisuals().then((competitionVisuals) => {
                    competitionVisuals.forEach((visual) => {
                        if (
                            visual.title === 'Family Slicer' ||
                            visual.title === 'Product Type Slicer' ||
                            visual.title === 'Button' ||
                            visual.title === 'Company Name'
                        ) {
                            visual.getFilters().then((currentFilters) => {
                                visual.updateFilters(
                                    models.FiltersOperations.Replace,
                                    [competitionFilter]
                                )
                            })
                        }
                    })
                })
            }
        })
    }

    useEffect(() => {
        updateAppHeaderContent(title)
    }, [updateAppHeaderContent])

    useEffect(() => {
        getEmbedReport({
            variables: {
                reportInput: {
                    workspaceId:
                        process.env.REACT_APP_ReportingDefaultEmbedWorkspaceId,
                    reportId:
                        process.env.REACT_APP_ReportingDefaultEmbedReportId,
                },
            },
        })
    }, [])

    return (
        <div
            id="reportContainer"
            data-testid="powerBiEmbedComponent"
            className={classes.mainContainer}
        >
            <PowerBIEmbed
                embedConfig={reportConfig}
                eventHandlers={eventHandlersMap}
                cssClassName={clsx(classes.reportStyle)}
                getEmbeddedComponent={(embedObject: Embed) => {
                    setReport(embedObject as Report)
                    console.log(
                        `Embedded object of type "${embedObject.embedtype}" received`
                    )
                }}
            />
        </div>
    )
}
export default SpecpointAnalyticsPage
